import type { AgentStatus } from '@entities/agent-status';
import type {
  MigratedCredentials,
  User,
  UserInfo,
  UserPhoneNumber,
} from '@entities/user';

import type { UserRepository } from '@infra/repositories/user.repository';

export class MemUserRepository implements UserRepository {
  private _user?: User;
  private _outboundNumber?: UserPhoneNumber;
  private _crmPreviousStatus?: string;
  private _phoneStatus?: AgentStatus;
  private _crmCurrentStatus?: string;

  get user() {
    return this._user as User;
  }

  getUserInfo(): UserInfo | undefined {
    return this._user?.info;
  }

  async addUserDetails(user: User): Promise<void> {
    this._user = user;
  }

  async removeUserDetails(): Promise<void> {
    this._user = undefined;
  }

  async getCRMPreviousStatus(): Promise<string | undefined> {
    return this._crmPreviousStatus;
  }

  async setCRMPreviousStatus(status: string): Promise<void> {
    this._crmPreviousStatus = status;
  }

  async getCurrentCRMStatus(): Promise<string | void> {
    if (this._crmCurrentStatus) return this._crmCurrentStatus;
  }

  async setCurrentCRMStatus(status: string): Promise<void> {
    this._crmCurrentStatus = status;
  }

  async setPhoneStatus(status: AgentStatus): Promise<void> {
    this._phoneStatus = status;
  }

  async getPhoneStatus(): Promise<AgentStatus | undefined> {
    return this._phoneStatus;
  }

  async cleanPreviousCRMStatus(): Promise<void> {
    this._crmPreviousStatus = undefined;
  }

  async getOutboundNumber(): Promise<string> {
    return (
      this._outboundNumber?.normalized ||
      (this.user as User).info.numbers.filter(
        (num) => num.id === this.user.info.default_out_number_id,
      )[0].normalized
    );
  }

  async updateNumber(number: string, id: number): Promise<void> {
    this._outboundNumber = this.user.info.numbers.filter(
      (num) => num.id === id || num.normalized === number,
    )[0];
  }

  async updateUserInfo(info: UserInfo): Promise<void> {
    if (this._user) {
      this._user.info = info;
    }
  }

  async getOutboundUserPhoneNumber(): Promise<UserPhoneNumber> {
    if (!this._outboundNumber) {
      await this.updateNumber('', this.user.info.default_out_number_id);
    }

    return this._outboundNumber as UserPhoneNumber;
  }

  async getPhoneNumberByNormalizedNumber(
    normalized: string,
  ): Promise<UserPhoneNumber> {
    return this.user.info.numbers.filter(
      (num) =>
        num.normalized.replace(/\D+/g, '') === normalized.replace(/\D+/g, ''),
    )[0];
  }

  updateTokens(tokens: MigratedCredentials) {
    if (this.user) this.user.credentials = tokens;
  }
}
